import { z } from "zod";
import { zId } from "./common.js";

export const SMS_CONFIG_STATUSES = ["provisioning", "pending_verification", "active", "failed"] as const;
export const zSmsConfigStatus = z.enum(SMS_CONFIG_STATUSES);
export type SmsConfigStatus = z.infer<typeof zSmsConfigStatus>;

/** the one form an org fills to enable texting — CauseTide provisions everything else */
export const zSmsSetupRequest = z.object({
  legalName: z.string().trim().min(2).max(200),
  website: z.string().trim().url().max(300),
  useCase: z.string().trim().min(20).max(1000).describe("what the org will text donors about"),
});
export type SmsSetupRequest = z.infer<typeof zSmsSetupRequest>;

/** a text is 160 GSM chars per segment; cap broadcasts at 4 segments */
export const SMS_MAX_BODY = 640;

export const zCreateSmsSend = z.object({
  name: z.string().trim().min(1).max(200),
  segmentId: zId,
  body: z.string().trim().min(1).max(SMS_MAX_BODY),
});
export type CreateSmsSend = z.infer<typeof zCreateSmsSend>;

/** words carriers reserve for opt-out/opt-in/help handling — never usable as keywords */
export const SMS_RESERVED_WORDS = ["STOP", "STOPALL", "UNSUBSCRIBE", "QUIT", "CANCEL", "END", "START", "UNSTOP", "YES", "HELP", "INFO"] as const;

export const zUpsertSmsKeyword = z.object({
  keyword: z
    .string()
    .trim()
    .min(2)
    .max(20)
    .regex(/^[a-z0-9]+$/i, "Letters and numbers only")
    .transform((s) => s.toUpperCase())
    .refine((s) => !SMS_RESERVED_WORDS.includes(s as (typeof SMS_RESERVED_WORDS)[number]), "That word is reserved by carriers"),
  pageId: zId.nullable().optional(),
  replyTemplate: z.string().trim().min(1).max(320).describe("supports {{link}} and {{org}}"),
  active: z.boolean().default(true),
});
export type UpsertSmsKeyword = z.infer<typeof zUpsertSmsKeyword>;

export const zSendPersonalSms = z.object({
  constituentId: zId,
  body: z.string().trim().min(1).max(SMS_MAX_BODY),
});
export type SendPersonalSms = z.infer<typeof zSendPersonalSms>;

/** staff-recorded verbal/written opt-in (TCPA evidence) */
export const zRecordSmsConsent = z.object({
  constituentId: zId,
  note: z.string().trim().min(3).max(500).describe("how consent was obtained"),
});
export type RecordSmsConsent = z.infer<typeof zRecordSmsConsent>;

/** "simulated": sent from a sandbox org — recorded, never handed to the carrier (CT-RM-22) */
export const SMS_MESSAGE_STATUSES = ["queued", "sending", "sent", "delivered", "failed", "received", "simulated"] as const;
export const zSmsMessageStatus = z.enum(SMS_MESSAGE_STATUSES);
export type SmsMessageStatus = z.infer<typeof zSmsMessageStatus>;
